/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { Phone } from 'phosphor-react';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';
import { ReturnButton } from '../../buttons/ReturnButton';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { MaskedInputForm } from '../../inputs/forms/MaskedInputForm';
import { PasswordInputForm } from '../../inputs/forms/PasswordInputForm';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import AlertModal from '../../AlertModal';
import { PageTitle } from '../../PageTitle';
import { FormBox } from '../../formBox';
import { FormHeading } from '../../formHeading';
import { getFormattedLabel } from '../../../utils/FormatFunctions';
import { mapMultiSelectToRelation } from '../../../utils/formMappers';

interface UserFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: string;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function UserForm({ defaultValues = {} }: UserFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/users';
  const { requestApi: reqApiUsers, error: errorUsers } = useFetch(baseUrl, {
    actionFeedback: false,
  });
  const {
    requestApi: reqApiGroups,
    data: dataGroups,
    loading: loadingGroups,
  } = useFetch('/v1/admin/groups');
  const { data: optionsGroups, pagination: paginationGroups } = getFetchData(dataGroups);
  const {
    requestApi: reqApiInstitutions,
    data: dataInstitutions,
    loading: loadingInstitutions,
  } = useFetch('/v1/admin/institutions');
  const { data: optionsInstitutions, pagination: paginationInstitutions } =
    getFetchData(dataInstitutions);
  const {
    requestApi: reqApiSecretaries,
    data: dataSecretaries,
    loading: loadingSecretaries,
  } = useFetch('/v1/admin/secretaries');
  const { data: optionsSecretaries, pagination: paginationSecretaries } =
    getFetchData(dataSecretaries);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState<any>('');
  const [uploading, setUploading] = useState<any>(false);
  const [useDefaultPassword, setUseDefaultPassword] = useState<boolean>(true);
  const [selectedGroup, setSelectedGroup] = useState<any>(defaultValues?.group || null);
  const [, setSelectedInstitutions] = useState<any[]>([]);
  const [, setSelectedSecretaries] = useState<any[]>([]);

  const optionsGenre = [
    {
      label: 'Masculino',
      value: 'M',
    },
    {
      label: 'Feminino',
      value: 'F',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do usuário',
      label: 'Nome',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .min(3, 'Nome deve ter pelo menos 3 caracteres'),
    },
    {
      name: 'email',
      value: defaultValues.email ?? '',
      placeholder: 'usuario@email.com',
      label: 'Email',
      yup: Yup.string().email('Email inválido').required('Este campo é obrigatório!'),
    },
    {
      name: 'cell_phone',
      value: defaultValues.cell_phone ?? '',
      placeholder: '(99) 99999-9999',
      label: 'Telefone',
      yup: Yup.string(),
    },
    {
      name: 'taxpayer',
      value: defaultValues.taxpayer ?? '',
      placeholder: '999.999.999-99',
      label: 'CPF',
      yup: Yup.string(),
      // yup: Yup.string().test('cpf', 'CPF inválido', (val) => (val ? isCPF(val) : true)),
    },
    {
      name: 'genre',
      value: defaultValues?.genre
        ? optionsGenre.find((item) => item.value === defaultValues?.genre)
        : null,
      placeholder: 'Gênero do usuário',
      label: 'Gênero',
      yup: Yup.mixed(),
    },
    {
      name: 'group_id',
      value: defaultValues?.group ? getFormattedLabel(defaultValues?.group) : null,
      placeholder: 'Grupo do usuário',
      label: 'Grupo',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'password',
      value: '',
      placeholder: 'Senha',
      label: 'Senha',
      yup: Yup.string(),
    },
    {
      name: 'confirm_password',
      value: '',
      placeholder: 'Digite a senha novamente',
      label: 'Confirmação de senha',
      yup: defaultValues.name
        ? Yup.string()
        : Yup.string().oneOf([Yup.ref('password'), null], 'Senhas não iguais'),
    },
    {
      name: 'image',
      value: defaultValues.image?.original ?? null,
      placeholder: '',
      label: 'Foto de perfil',
      yup: Yup.mixed().nullable(),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  yupschema.use_default_password_switch = Yup.boolean();
  defaultValuesSchema.use_default_password_switch = true;

  // Adicionar campos condicionais ao schema
  yupschema.institutions = Yup.mixed();
  yupschema.secretaries = Yup.mixed();
  yupschema.secretary_id = Yup.mixed();
  yupschema.institution_id = Yup.mixed();

  defaultValuesSchema.institutions =
    defaultValues?.institutions?.map((inst: any) => getFormattedLabel(inst)) ?? [];
  defaultValuesSchema.secretaries =
    defaultValues?.secretaries?.map((sec: any) => getFormattedLabel(sec)) ?? [];
  defaultValuesSchema.secretary_id = defaultValues?.secretary
    ? getFormattedLabel(defaultValues.secretary)
    : null;
  defaultValuesSchema.institution_id = defaultValues?.institution
    ? getFormattedLabel(defaultValues.institution)
    : null;

  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const email = formInputs.find((field) => field.name === 'email');
  const cellphone = formInputs.find((field) => field.name === 'cell_phone');
  const taxpayer = formInputs.find((field) => field.name === 'taxpayer');
  const genre = formInputs.find((field) => field.name === 'genre');
  const groupId = formInputs.find((field) => field.name === 'group_id');
  // const password = formInputs.find(field => field.name === 'password');
  // const confirmPassword = formInputs.find(field => field.name === 'confirm_password');
  const image = formInputs.find((field) => field.name === 'image');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const params: any = {
      ...values,
      genre: values.genre?.value ?? null,
      password: (() => {
        if (defaultValues.name) return _;
        if (useDefaultPassword) return '123456';
        return values.password;
      })(),
      use_default_password: !defaultValues.name && useDefaultPassword,
      group_id: values.group_id.value,
      confirm_password: _,
      image: _,
    };

    // Adicionar vínculos baseados no grupo
    if (isTutorOrTeacher && values.institutions) {
      const mappedInstitutions = mapMultiSelectToRelation(values.institutions);
      if (mappedInstitutions.length) {
        params.institutions = mappedInstitutions;
      }
    }

    if (isResponsible) {
      if (values.secretaries) {
        const mappedSecretaries = mapMultiSelectToRelation(values.secretaries);
        if (mappedSecretaries.length) {
          params.secretaries = mappedSecretaries;
        }
      }
      if (values.institutions) {
        const mappedInstitutions = mapMultiSelectToRelation(values.institutions);
        if (mappedInstitutions.length) {
          params.institutions = mappedInstitutions;
        }
      }
    }

    if (isSecretaryResponsible) {
      if (values.secretary_id) {
        const mappedSecretary = mapMultiSelectToRelation(values.secretary_id);
        if (mappedSecretary.length) {
          params.secretaries = mappedSecretary;
        }
      }
      if (values.institutions) {
        const mappedInstitutions = mapMultiSelectToRelation(values.institutions);
        if (mappedInstitutions.length) {
          params.institutions = mappedInstitutions;
        }
      }
    }

    if (isInstitutionResponsible) {
      if (values.secretary_id) {
        const mappedSecretary = mapMultiSelectToRelation(values.secretary_id);
        if (mappedSecretary.length) {
          params.secretaries = mappedSecretary;
        }
      }
      if (values.institution_id) {
        const mappedInstitution = mapMultiSelectToRelation(values.institution_id);
        if (mappedInstitution.length) {
          params.institutions = mappedInstitution;
        }
      }
    }

    // Remover campos auxiliares que não devem ser enviados
    delete params.secretary_id;
    delete params.institution_id;

    const { status: resStatus, data } = await reqApiUsers(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if ((resStatus === 200 || resStatus === 201) && images) {
      setUploading(true);
      const apiUrlId = itemId ?? data?.data?.id;
      await reqApiUsers('put', { file: images.toString() }, `${baseUrl}/${apiUrlId}/images`);
      setUploading(false);
      onOpen();
    } else {
      onOpen();
    }
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
      setSelectedGroup(defaultValues?.group || null);

      // Carregar instituições vinculadas
      if (defaultValues?.institutions) {
        setSelectedInstitutions(
          defaultValues.institutions.map((inst: any) => ({
            id: inst.id,
            name: inst.name,
          }))
        );
      }

      // Carregar secretarias vinculadas
      if (defaultValues?.secretaries) {
        setSelectedSecretaries(
          defaultValues.secretaries.map((sec: any) => ({
            id: sec.id,
            name: sec.name,
          }))
        );
      }
    }
  }, [defaultValues]);

  // Monitora mudança no grupo selecionado
  const watchGroup = watch('group_id');
  useEffect(() => {
    if (watchGroup?.value) {
      const group = optionsGroups?.find((g: any) => g.id === watchGroup.value);
      setSelectedGroup(group || null);
    }
  }, [watchGroup, optionsGroups]);

  // Verifica se o grupo é Admin ou Sistema
  const isAdminOrSystem =
    selectedGroup?.name === 'Administrador' || selectedGroup?.name === 'Sistema';

  // Verifica se o grupo é Tutor ou Professor
  const isTutorOrTeacher = selectedGroup?.name === 'Tutor' || selectedGroup?.name === 'Professor';

  // Verifica se é Responsável
  const isResponsible = selectedGroup?.name === 'Responsável';

  // Verifica se é Responsável da secretaria
  const isSecretaryResponsible = selectedGroup?.name === 'Responsável da secretaria';

  // Verifica se é Responsável da instituição
  const isInstitutionResponsible = selectedGroup?.name === 'Responsável da instituição';

  return (
    <FormBox>
      <Flex>
        <ReturnButton previousPage="users" mb={5} />
        <PageTitle>{defaultValues.name ? 'Editar' : 'Criar'} Usuário</PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!errorUsers
              ? `Usuário ${defaultValues.name ? 'editado' : 'criado'} com sucesso!`
              : errorUsers?.msg || `Erro ao ${defaultValues.name ? 'editar' : 'criar'} o usuário.`}
          </Text>
        }
        msgStatus={errorUsers ? 'error' : 'success'}
        closeOnOverlayClick={!!errorUsers}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate('/users')}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Básicas</FormHeading>
        <Flex gap={5}>
          <Box flex={1}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              name={email?.name}
              label={email?.label}
              placeholder={email?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </Box>
        </Flex>

        <Flex gap={5}>
          <Box flex={2}>
            <MaskedInputForm
              name={cellphone?.name}
              label={cellphone?.label}
              placeholder={cellphone?.placeholder}
              control={control}
              errors={errors}
              mask="(99) 99999-9999"
              iconLeft={<Phone />}
            />
          </Box>

          <Box flex={2}>
            <SelectInputForm
              name={groupId?.name}
              label={groupId?.label}
              options={[...(optionsGroups?.map((item) => getFormattedLabel(item)) ?? [])]}
              isLoading={loadingGroups}
              pagination={paginationGroups}
              getOptions={(filters) => reqApiGroups('get', { params: { ...filters } })}
              isRequired
              placeholder={groupId?.placeholder}
              control={control}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </Box>
        </Flex>

        <Flex gap={5}>
          <Box flex={2}>
            <MaskedInputForm
              name={taxpayer?.name}
              label={taxpayer?.label}
              placeholder={taxpayer?.placeholder}
              control={control}
              errors={errors}
              mask="999.999.999-99"
            />
          </Box>

          <Box flex={2}>
            <SelectInputForm
              name={genre?.name}
              label={genre?.label}
              options={optionsGenre}
              placeholder={genre?.placeholder}
              control={control}
              errors={errors}
            />
          </Box>
        </Flex>

        {!defaultValues?.name && (
          <>
            <FormHeading>Segurança</FormHeading>
            <Box mb={4}>
              <SwitchInputForm
                name="use_default_password_switch"
                label="Usar senha padrão (123456)"
                onChange={(e: any) => setUseDefaultPassword(e.target.checked)}
                control={control}
                errors={errors}
              />
            </Box>
            {!useDefaultPassword && (
              <Flex gap={5}>
                <Box flex={1}>
                  <PasswordInputForm
                    name="password"
                    label="Senha"
                    placeholder="Digite a senha"
                    isRequired
                    register={register}
                    errors={errors}
                  />
                </Box>

                <Box flex={1}>
                  <PasswordInputForm
                    name="confirm_password"
                    label="Confirmação de senha"
                    placeholder="Digite a senha novamente"
                    isRequired
                    register={register}
                    errors={errors}
                  />
                </Box>
              </Flex>
            )}
          </>
        )}

        {/* Campos condicionais baseados no grupo */}
        {!isAdminOrSystem &&
          (isTutorOrTeacher ||
            isResponsible ||
            isSecretaryResponsible ||
            isInstitutionResponsible) && (
            <>
              <FormHeading>Vínculos</FormHeading>

              {/* Tutor ou Professor: mostrar instituições */}
              {isTutorOrTeacher && (
                <Box mb={4}>
                  <SelectInputForm
                    name="institutions"
                    label="Instituições Vinculadas"
                    options={[
                      ...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? []),
                    ]}
                    isLoading={loadingInstitutions}
                    isMulti
                    pagination={paginationInstitutions}
                    getOptions={(filters) => reqApiInstitutions('get', { params: { ...filters } })}
                    placeholder="Selecione as instituições"
                    control={control}
                    errors={errors}
                  />
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {defaultValues.name
                      ? 'Instituições vinculadas a este usuário'
                      : 'Selecione as instituições que este usuário terá acesso'}
                  </Text>
                </Box>
              )}

              {/* Responsável: mostrar secretaria e instituição */}
              {isResponsible && (
                <>
                  <Box mb={4}>
                    <SelectInputForm
                      name="secretaries"
                      label="Secretarias Vinculadas"
                      options={[
                        ...(optionsSecretaries?.map((item) => getFormattedLabel(item)) ?? []),
                      ]}
                      isLoading={loadingSecretaries}
                      isMulti
                      pagination={paginationSecretaries}
                      getOptions={(filters) => reqApiSecretaries('get', { params: { ...filters } })}
                      placeholder="Selecione as secretarias"
                      control={control}
                      errors={errors}
                    />
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Secretarias vinculadas (opcional)
                    </Text>
                  </Box>

                  <Box mb={4}>
                    <SelectInputForm
                      name="institutions"
                      label="Instituições Vinculadas"
                      options={[
                        ...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? []),
                      ]}
                      isLoading={loadingInstitutions}
                      isMulti
                      pagination={paginationInstitutions}
                      getOptions={(filters) =>
                        reqApiInstitutions('get', { params: { ...filters } })
                      }
                      placeholder="Selecione as instituições"
                      control={control}
                      errors={errors}
                    />
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Instituições vinculadas (opcional)
                    </Text>
                  </Box>
                </>
              )}

              {/* Responsável da secretaria */}
              {isSecretaryResponsible && (
                <>
                  <Box mb={4}>
                    <SelectInputForm
                      name="secretary_id"
                      label="Secretaria"
                      options={[
                        ...(optionsSecretaries?.map((item) => getFormattedLabel(item)) ?? []),
                      ]}
                      isLoading={loadingSecretaries}
                      pagination={paginationSecretaries}
                      getOptions={(filters) => reqApiSecretaries('get', { params: { ...filters } })}
                      placeholder="Selecione a secretaria"
                      control={control}
                      errors={errors}
                    />
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Secretaria que este responsável gerencia
                    </Text>
                  </Box>

                  <Box mb={4}>
                    <SelectInputForm
                      name="institutions"
                      label="Instituições da Secretaria"
                      options={[
                        ...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? []),
                      ]}
                      isLoading={loadingInstitutions}
                      isMulti
                      pagination={paginationInstitutions}
                      getOptions={(filters) =>
                        reqApiInstitutions('get', { params: { ...filters } })
                      }
                      placeholder="Selecione as instituições"
                      control={control}
                      errors={errors}
                    />
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Instituições vinculadas (opcional)
                    </Text>
                  </Box>
                </>
              )}

              {/* Responsável da instituição */}
              {isInstitutionResponsible && (
                <>
                  <Box mb={4}>
                    <SelectInputForm
                      name="secretary_id"
                      label="Secretaria Vinculada"
                      options={[
                        ...(optionsSecretaries?.map((item) => getFormattedLabel(item)) ?? []),
                      ]}
                      isLoading={loadingSecretaries}
                      pagination={paginationSecretaries}
                      getOptions={(filters) => reqApiSecretaries('get', { params: { ...filters } })}
                      placeholder="Selecione a secretaria"
                      control={control}
                      errors={errors}
                    />
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Secretaria da instituição
                    </Text>
                  </Box>

                  <Box mb={4}>
                    <SelectInputForm
                      name="institution_id"
                      label="Instituição"
                      options={[
                        ...(optionsInstitutions?.map((item) => getFormattedLabel(item)) ?? []),
                      ]}
                      isLoading={loadingInstitutions}
                      pagination={paginationInstitutions}
                      getOptions={(filters) =>
                        reqApiInstitutions('get', { params: { ...filters } })
                      }
                      isRequired
                      placeholder="Selecione a instituição"
                      control={control}
                      errors={errors}
                    />
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Instituição que este responsável gerencia
                    </Text>
                  </Box>
                </>
              )}
            </>
          )}

        <Box mt={5}>
          <ImageInputForm
            defaultVal={defaultValues?.image}
            setImages={setImages}
            setIsUpping={setUploading}
            name={image?.name}
            label={image?.label}
            register={register}
            watch={watch}
            errors={errors}
          />
        </Box>

        <Flex mr="auto">
          <FormButton
            mt={4}
            w="100px"
            isLoading={isSubmitting || uploading}
            type="submit"
            textTransform="uppercase"
          >
            Salvar
          </FormButton>
        </Flex>
      </form>
    </FormBox>
  );
}
